import { AuthApi, UserAccess } from 'api';
import appRoutes from 'app.routes';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import PageLayout from './PageLayout';
import { PageLayoutType } from './types';
import { useAuth, useMindworkingUrl } from 'modules/auth/hooks';
import { useLocalization } from 'localization';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useRefreshToken } from 'hooks/general/useRefreshToken';
import { Helmet } from 'react-helmet';

interface PageProps extends PropsWithChildren {
  layout?: PageLayoutType;
  guard?: boolean | 'admin';
  titleKey: string;
}

const Page: FC<PageProps> = ({ layout = 'app', guard, titleKey, children }) => {
  const { t } = useLocalization();
  const userAuth = useAuth();
  const userIsAdmin =
    !userAuth.user?.isInternalUser ||
    !!userAuth.user?.branches.some(branch => branch.access === UserAccess.Admin) ||
    userAuth.userIsLoading;
  const isGuarded = (guard && !AuthApi.isAuthenticated()) || (guard == 'admin' && !userIsAdmin);
  const [redirectChecked, setRedirectChecked] = useState(false);
  const { isMindworking } = useMindworkingUrl();
  const navigate = useNavigate();
  const location = useLocation();

  //converted iframe jsx to custom hook
  useRefreshToken();

  const debouncedNavigation = useDebouncedCallback(() => {
    if (guard == 'admin') {
      navigate(appRoutes.dashboard(), { replace: true });
      return;
    }

    userAuth.persistRedirectUrl();

    if (isMindworking) return;

    navigate(appRoutes.login(), { replace: true });
  }, 1000);

  useEffect(() => {
    if (document.location.hash.includes('#/interview')) {
      const params = document.location.hash.split('?')[1] || '';
      window.location.href = `${window.location.origin}/interview?${params}`;
      return;
    }
    if (document.location.hash.includes('#/adduser')) {
      const params = document.location.hash.split('?')[1] || '';
      window.location.href = `${window.location.origin}/accept-invite?${params}`;
      return;
    }
    if (document.location.hash.includes('#/presentation')) {
      const params = document.location.hash.split('?')[1];
      if (params) {
        const query = new URLSearchParams(params);
        const { caseId, clientId, filter } = {
          caseId: query.get('r'),
          clientId: query.get('c'),
          filter: query.get('v'),
        };
        window.location.href = `${window.location.origin}/presentation?${caseId ? `caseId=${caseId}&` : ''}${
          clientId ? `clientId=${clientId}&` : ''
        }${filter ? `filter=${filter}` : ''}`;
        return;
      }
    }
    if (document.location.hash.includes('#/booking')) {
      const params = document.location.hash.split('?')[1] || '';
      window.location.href = `${window.location.origin}/booking?${params}`;
      return;
    }
    if (document.location.hash.includes('#/resetpassword')) {
      const params = document.location.hash.split('?')[1] || '';
      window.location.href = `${window.location.origin}/reset-password?${params}`;
      return;
    }
    setRedirectChecked(true);
  }, []);

  useEffect(() => {
    if (isGuarded) {
      debouncedNavigation();
    }
    return () => debouncedNavigation.cancel();
  }, [isGuarded]);

  if (isGuarded || !redirectChecked) {
    return null;
  }

  return (
    <>
      <Helmet key={titleKey}>
        <title>{t(titleKey)}</title>
      </Helmet>
      <PageLayout.Consumer
        contentKey={location.pathname}
        type={layout}
        systemMessage={
          userAuth.user?.branches?.[0]?.countryCode === 'DK' ? t('common.messages.notificationHeader') : ''
        }
      >
        {children}
      </PageLayout.Consumer>
    </>
  );
};

export default Page;
